<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { sentry } from '$shared/helpers/logger/sentry';
  import { ComponentException } from '$shared/exceptions/ComponentExpection';
  import UIErrorMessage from '$shared/constants/UIErrorMessage';
  import { UIError } from '$provider/models/error/UIError';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import type { GoogleButtonOptions } from './GoogleAuthButton.types';

  const GOOGLE_GSI_URL = 'https://accounts.google.com/gsi/client';
  const GOOGLE_GSI_NONCE = '251272CFEA898500FF84A0BDC2256455E63B371';

  const BUTTON_VISIBILITY_DELAY_MS: number = 2000;

  const GOOGLE_BUTTON_OPTIONS: GoogleButtonOptions = {
    theme: 'outline',
    size: 'large',
    width: '315px',
    text: 'signin_with',
    logo_alignment: 'left'
  };

  interface Props {
    clientId?: string;
    tabIndex?: number;
    onsuccess?: (credential: string) => void;
    onerror?: (message: string) => void;
  }

  let { clientId = '', tabIndex = 1, onsuccess, onerror }: Props = $props();

  let buttonDivElement: HTMLDivElement | undefined = $state();

  let buttonVisible = $state(false);

  let cancelled = false;

  onMount(() => {
    cancelled = false;

    const run = async () => {
      try {
        await loadGoogleClientScriptAsync();
        if (cancelled) return;

        window?.google?.accounts?.id?.initialize({
          client_id: clientId,
          callback: handleCredentialResponse
        });
        window?.google?.accounts?.id?.renderButton(buttonDivElement, GOOGLE_BUTTON_OPTIONS);

        // Show button with slight delay (2000ms) to avoid layout shift when the Google button is rendered
        // Also this is a patch, not a perfect solution, because the site blocks google button inline style because of CSP, to fix that issue we need to add a unsafe-inline in CSP for google button styles
        setTimeout(() => {
          if (!cancelled) buttonVisible = true;
        }, BUTTON_VISIBILITY_DELAY_MS);
      } catch (err: any) {
        if (cancelled) return;
        const errorObject =
          (err?.message && err) || new UIError(UIErrorMessage.FailedToLoadGoogleSiginButton);

        onerror?.(errorObject?.message);
        const expception = ComponentException.from(
          'GoogleAuthButton',
          'onMount',
          { clientId, tabIndex },
          errorObject,
          { error: err }
        );
        sentry.log(expception);
      }
    };

    run();
  });

  onDestroy(() => {
    cancelled = true;
  });

  const loadGoogleClientScriptAsync = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      // Already in the DOM — resolve immediately so we don't append a duplicate.
      if (document.querySelector(`script[src="${GOOGLE_GSI_URL}"]`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = GOOGLE_GSI_URL;
      script.setAttribute('nonce', GOOGLE_GSI_NONCE);
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${GOOGLE_GSI_URL}`));
      document.head.appendChild(script);
    });
  };

  const handleCredentialResponse = ({ credential }: any): void => {
    onsuccess?.(credential);
  };
</script>

<div class="comp-google-auth-button">
  <div class="buttonBase" id="GoogleAuth" bind:this={buttonDivElement} class:show={buttonVisible}>
    {''}
  </div>
  {#if !buttonVisible}
    <div class="button-spinner">
      <Spinner />
    </div>
  {/if}
</div>

<style>
  .comp-google-auth-button .buttonBase {
    width: 100%;
    display: none;
    align-items: center;
    justify-content: center;
  }
  .comp-google-auth-button .buttonBase.show {
    display: flex;
  }
  .comp-google-auth-button .button-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
